import { Image as ImageIcon, Maximize2, Save, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { api } from '../lib/api';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ZoomableImage from '../components/ZoomableImage';
import { Sparkles } from 'lucide-react';

export default function Diagrams() {
    const location = useLocation();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const { user, login, isLimitReached, incrementQuestions } = useAuth();

    useEffect(() => {
        const initialSearch = (location.state as any)?.initialSearch;
        if (initialSearch) {
            setQuery(initialSearch);
            performSearch(initialSearch);
        }
    }, [location.state]);

    const performSearch = async (searchQuery: string) => {
        if (!searchQuery.trim()) return;
        if (isLimitReached) return;
        setLoading(true);
        try {
            const data = await api.searchDiagrams(searchQuery);
            setResults(data.diagrams || data.results || []);
            incrementQuestions();
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => performSearch(query);

    return (
        <div className="flex-1 overflow-y-auto p-6">
            <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                    <h2 className="text-3xl font-black text-white uppercase tracking-tighter">System Schematics</h2>
                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            placeholder="System name (e.g. 'brake booster assembly')..."
                            className="flex-1 bg-dark border border-slate/30 rounded-xl px-4 py-4 text-white focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none"
                        />
                        <button
                            onClick={handleSearch}
                            disabled={loading || !query.trim()}
                            className="bg-secondary text-white px-10 rounded-xl font-bold uppercase tracking-widest hover:bg-secondary/90 transition-all disabled:opacity-50"
                        >
                            {loading ? 'Retrieving...' : 'Fetch'}
                        </button>
                        <button
                            onClick={() => {
                                if (!user) {
                                    const email = prompt('Sign in to save schematics. Enter your email:');
                                    if (email) login(email);
                                    return;
                                }
                                if (results.length === 0) return;
                                api.saveProject({
                                    id: Date.now().toString(),
                                    name: query,
                                    type: 'diagrams',
                                    data: { results }
                                });
                                alert('Schematics saved to Projects.');
                            }}
                            disabled={results.length === 0}
                            title="Save Search"
                            className="p-4 bg-dark border border-slate/30 rounded-xl text-gray-500 hover:text-accent hover:border-accent/50 transition-all disabled:opacity-30"
                        >
                            <Save size={20} />
                        </button>
                    </div>
                </div>

                {results.length > 0 && (
                    <div className="bg-primary/5 border border-primary/20 rounded-3xl p-6 flex items-center justify-between animate-in slide-in-from-bottom-2">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-primary/10 rounded-2xl">
                                <Sparkles className="text-primary" size={24} />
                            </div>
                            <div>
                                <h4 className="text-white font-black uppercase tracking-widest text-sm">Master-Tier Refinement</h4>
                                <p className="text-xs text-secondary font-bold">Use Gemini Flash Vision to synthesize these {results.length} references into a single Master Diagram.</p>
                            </div>
                        </div>
                        <button
                            onClick={async () => {
                                setGenerating(true);
                                try {
                                    const diag = await api.generateVisionDiagram(query, {}, results.map(r => r.url).slice(0, 10));
                                    if (diag.success) {
                                        setResults([diag.diagram, ...results]);
                                        setSelectedImage(diag.diagram.url);
                                        incrementQuestions();
                                    }
                                } catch (e) {
                                    console.error(e);
                                    alert('Vision synthesis failed. Ensure GOOGLE_API_KEY is active.');
                                } finally {
                                    setGenerating(false);
                                }
                            }}
                            disabled={generating}
                            className={`px-6 py-3 rounded-xl font-black uppercase tracking-widest text-xs transition-all ${generating ? 'bg-gray-800 text-gray-500' : 'bg-primary text-dark hover:bg-white shadow-lg shadow-primary/20'}`}
                        >
                            {generating ? 'Synthesizing...' : 'Generate Flash Master'}
                        </button>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {results.map((diagram, i) => (
                        <div key={i} className={`bg-dark/40 border rounded-2xl overflow-hidden p-6 space-y-4 hover:border-secondary/30 transition-all flex flex-col justify-between ${diagram.generated ? 'border-primary/50 shadow-lg shadow-primary/5' : 'border-slate/20'}`}>
                            <div className="space-y-4">
                                <div className="flex justify-between items-start">
                                    <h3 className="text-white text-sm font-bold tracking-tight line-clamp-1">
                                        {diagram.generated && <span className="text-primary mr-2">✦</span>}
                                        {diagram.title || 'Schematic Rendering'}
                                    </h3>
                                    {diagram.generated && (
                                        <span className="text-[8px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-black uppercase tracking-widest">Flash AI</span>
                                    )}
                                </div>

                                <div
                                    className="aspect-square bg-black/80 rounded-xl overflow-hidden flex items-center justify-center border border-white/5 shadow-inner relative group/img cursor-zoom-in"
                                    onClick={() => (diagram.url || diagram.image_url) && setSelectedImage(diagram.url || diagram.image_url)}
                                >
                                    {(diagram.url || diagram.image_url) ? (
                                        <img src={diagram.url || diagram.image_url} alt={diagram.title} className="w-full h-full object-contain p-2 group-hover/img:scale-110 transition-transform duration-700" />
                                    ) : (
                                        <div className="flex flex-col items-center gap-2 text-gray-700">
                                            <ImageIcon size={48} />
                                            <span className="text-[10px] uppercase font-bold tracking-widest">Metadata Only</span>
                                        </div>
                                    )}
                                    <div className="absolute inset-0 bg-primary/0 group-hover/img:bg-primary/5 transition-colors flex items-center justify-center">
                                        <Maximize2 size={24} className="text-white opacity-0 group-hover/img:opacity-100 transition-opacity translate-y-2 group-hover/img:translate-y-0 duration-300" />
                                    </div>
                                </div>

                                <p className="text-[11px] text-gray-500 leading-relaxed italic line-clamp-2">
                                    {diagram.description || 'Verified engineering schematic for identified system node.'}
                                </p>
                            </div>

                            <div className="pt-4 flex justify-between items-center border-t border-white/5">
                                <span className="text-[9px] uppercase font-black tracking-widest text-gray-600">{diagram.source || 'Archive'}</span>
                                <button
                                    onClick={() => setSelectedImage(diagram.url || diagram.image_url)}
                                    className="text-[9px] uppercase font-black tracking-widest text-primary hover:text-white transition-colors"
                                >
                                    Ghost Mode
                                </button>
                            </div>
                        </div>
                    ))}
                    {!loading && query && results.length === 0 && (
                        <div className="col-span-full py-20 text-center text-gray-500 italic">
                            No schematics present in target sector.
                        </div>
                    )}
                </div>
            </div>

            {/* Image Zoom Portal & Ghosting Engine */}
            {selectedImage && (
                <div className="fixed inset-0 z-[100] bg-dark/95 backdrop-blur-2xl flex items-center justify-center p-4 sm:p-12 animate-in fade-in duration-300">
                    <button
                        className="absolute top-8 right-8 z-[110] text-gray-400 hover:text-white transition-colors"
                        onClick={() => setSelectedImage(null)}
                    >
                        <X size={32} />
                    </button>

                    <div className="w-full max-w-6xl animate-in zoom-in-95 duration-500">
                        <ZoomableImage src={selectedImage} />
                    </div>
                </div>
            )}
        </div>
    );
}
